import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const NotificationsCommunications = ({ currentAdminId }) => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [targetAudience, setTargetAudience] = useState('all');
  const [notifType, setNotifType] = useState('system');
  const [sending, setSending] = useState(false);
  const [sentHistory, setSentHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [successMsg, setSuccessMsg] = useState('');

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const { data, error } = await supabase?.from('notifications')
        ?.select('id, title, message, notification_type, created_at, user_id')
        ?.order('created_at', { ascending: false })
        ?.limit(20);
      if (!error && data) {
        setSentHistory(data?.map(n => ({
          id: n?.id,
          title: n?.title,
          message: n?.message,
          type: n?.notification_type,
          date: new Date(n?.created_at)?.toLocaleString(),
          userId: n?.user_id,
        })));
      }
    } catch (err) {
      console.error('[NotificationsCommunications] loadHistory error:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleSend = async () => {
    if (!title?.trim() || !message?.trim()) return;
    setSending(true);
    try {
      // Get target users
      let query = supabase?.from('user_profiles')?.select('id');
      if (targetAudience === 'influencers') query = query?.eq('role', 'influencer');
      else if (targetAudience === 'brands') query = query?.eq('role', 'brand');
      else if (targetAudience === 'admins') query = query?.eq('role', 'admin');

      const { data: users, error: usersError } = await query;
      if (usersError) throw usersError;

      // Insert notifications for all target users
      const notifications = users?.map(u => ({
        user_id: u?.id,
        title: title?.trim(),
        message: message?.trim(),
        notification_type: notifType,
        is_read: false,
      }));

      if (notifications?.length > 0) {
        const { error } = await supabase?.from('notifications')?.insert(notifications);
        if (!error) {
          setSuccessMsg(`Notification sent to ${notifications?.length} user${notifications?.length !== 1 ? 's' : ''}!`);
          setTitle('');
          setMessage('');
          loadHistory();
          setTimeout(() => setSuccessMsg(''), 4000);
        }
      } else {
        setSuccessMsg('No users found for the selected audience.');
        setTimeout(() => setSuccessMsg(''), 3000);
      }
    } catch (err) {
      console.error('[NotificationsCommunications] handleSend error:', err);
    } finally {
      setSending(false);
    }
  };

  const audienceOptions = [
    { value: 'all', label: 'All Users' },
    { value: 'influencers', label: 'Influencers Only' },
    { value: 'brands', label: 'Brand Partners Only' },
    { value: 'admins', label: 'Admins Only' },
  ];

  const typeOptions = [
    { value: 'system', label: 'System Announcement' },
    { value: 'campaign', label: 'Campaign Update' },
    { value: 'payment', label: 'Payment Notice' },
    { value: 'moderation', label: 'Moderation Alert' },
  ];

  const typeBadge = (type) => {
    const map = {
      system: 'bg-primary/10 text-primary',
      campaign: 'bg-secondary/10 text-secondary',
      payment: 'bg-success/10 text-success',
      moderation: 'bg-warning/10 text-warning',
      score: 'bg-accent/10 text-accent',
      transaction: 'bg-muted text-muted-foreground',
    };
    return <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${map?.[type] || 'bg-muted text-muted-foreground'}`}>{type}</span>;
  };

  return (
    <div className="space-y-6">
      {/* Compose */}
      <div className="bg-card rounded-xl border border-border p-5">
        <h3 className="text-base font-semibold text-foreground mb-5">Send Notification</h3>

        {successMsg && (
          <div className="flex items-center gap-2 p-3 bg-success/10 border border-success/30 rounded-lg mb-4">
            <Icon name="CheckCircle" size={16} color="var(--color-success)" />
            <p className="text-sm text-success">{successMsg}</p>
          </div>
        )}

        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Target Audience</label>
              <Select options={audienceOptions} value={targetAudience} onChange={setTargetAudience} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Notification Type</label>
              <Select options={typeOptions} value={notifType} onChange={setNotifType} />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Title</label>
            <Input
              type="text"
              placeholder="Notification title..."
              value={title}
              onChange={(e) => setTitle(e?.target?.value)}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e?.target?.value)}
              placeholder="Write your message here..."
              rows={4}
              className="w-full bg-muted border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
            />
          </div>

          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Sending to: <span className="text-foreground font-medium">{audienceOptions?.find(a => a?.value === targetAudience)?.label}</span>
            </p>
            <Button
              variant="default"
              iconName="Send"
              iconPosition="left"
              onClick={handleSend}
              disabled={sending || !title?.trim() || !message?.trim()}
            >
              {sending ? 'Sending...' : 'Send Notification'}
            </Button>
          </div>
        </div>
      </div>

      {/* History */}
      <div className="bg-card rounded-xl border border-border p-5">
        <h3 className="text-base font-semibold text-foreground mb-4">Message History</h3>
        {historyLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : sentHistory?.length > 0 ? (
          <div className="space-y-3">
            {sentHistory?.map((n) => (
              <div key={n?.id} className="flex items-start gap-3 p-3 bg-muted/40 rounded-lg">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon name="Bell" size={14} color="var(--color-primary)" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-sm font-medium text-foreground">{n?.title}</p>
                    {typeBadge(n?.type)}
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2">{n?.message}</p>
                  <p className="text-xs text-muted-foreground mt-1">{n?.date}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Icon name="Bell" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No notifications sent yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsCommunications;
